import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import MapSelector from '@/components/MapSelector';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function GPXViewer({ user }) {
  const { eventId } = useParams();
  const [gpxData, setGpxData] = useState(null);
  const [coordinates, setCoordinates] = useState([]);
  const [center, setCenter] = useState([40.4168, -3.7038]); // Default: Madrid
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGPXContent();
  }, [eventId]);

  const fetchGPXContent = async () => {
    try {
      const response = await axios.get(`${API}/gpx/${eventId}/preview`);
      const content = response.data.content;
      setGpxData(content);
      parseGPX(content);
    } catch (error) {
      console.error('Error loading GPX:', error);
      toast.error('Error al cargar el track GPX');
    } finally {
      setLoading(false);
    }
  };

  const parseGPX = (gpxString) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(gpxString, 'text/xml');
    const trackPoints = xmlDoc.getElementsByTagName('trkpt');
    
    const coords = [];
    for (let i = 0; i < trackPoints.length; i++) {
      const lat = parseFloat(trackPoints[i].getAttribute('lat'));
      const lon = parseFloat(trackPoints[i].getAttribute('lon'));
      coords.push([lat, lon]);
    }
    
    if (coords.length > 0) {
      setCoordinates(coords);
      setCenter(coords[0]);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/gpx/${eventId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `track-${eventId}.gpx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Archivo GPX descargado');
    } catch (error) {
      toast.error('Error al descargar GPX');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando mapa...</div>;
  }

  if (!coordinates.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md" data-testid="no-gpx-data">
          <CardContent className="text-center py-12">
            <p className="text-gray-600">No se pudo cargar el track GPX</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="border-0 shadow-lg" data-testid="gpx-viewer-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-2xl">Track del Recorrido</CardTitle>
            <Button 
              onClick={handleDownload}
              variant="outline"
              data-testid="download-gpx-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar GPX
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-[600px] rounded-lg overflow-hidden relative" data-testid="map-container">
              {/* Map Style Selector */}
              <div className="absolute top-2 right-2 z-[1000]">
                <MapSelector compact />
              </div>
              
              <MapContainer 
                center={center} 
                zoom={13} 
                style={{ height: '100%', width: '100%' }}
              >
                <DynamicTileLayer maxZoom={17} />
                <Polyline 
                  positions={coordinates} 
                  color="#f97316" 
                  weight={4}
                  opacity={0.8}
                />
                {coordinates.length > 0 && (
                  <>
                    <Marker position={coordinates[0]}>
                      <Popup>Inicio del recorrido</Popup>
                    </Marker>
                    <Marker position={coordinates[coordinates.length - 1]}>
                      <Popup>Fin del recorrido</Popup>
                    </Marker>
                  </>
                )}
              </MapContainer>
            </div>
            <div className="mt-4 p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Nota:</strong> Este mapa utiliza tiles topográficos de OpenTopoMap que muestran carreteras, caminos y elevaciones. 
                Puedes descargar el archivo GPX para usarlo en tu dispositivo GPS o aplicación de navegación favorita.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
